/**
 * The icon set, and the one place an icon is named.
 *
 * `@fortawesome/free-solid-svg-icons` rather than a component library, and rather
 * than the webfont package, for a reason that decides the whole design: these are
 * **raw SVG path strings**, so the same icon works in two places that share
 * nothing —
 *
 *  - a DOM overlay, positioned over the world by `ScreenProjector` like a
 *    nameplate, and
 *  - a canvas, via `new Path2D(...)`, painted into a `DynamicTexture` beside the
 *    biome patterns in `game/zone/textures.ts`.
 *
 * A JSX component library (`lucide-solid`, the Tabler Solid package) cannot do the
 * second at all, and the FontAwesome *webfont* package is ~900 KB of binary font
 * for a dozen glyphs — which would also be the first binary asset in the client
 * since the music. Tree-shaken path data is a few hundred bytes each.
 *
 * The set is deliberately closed. An icon named in one place and drawn in another
 * is how a UI ends up with three arrows that mean different things.
 */

import {
  faArchway,
  faBottleDroplet,
  faDoorOpen,
  faDungeon,
  faFeather,
  faGem,
  faHammer,
  faLocationDot,
  faShieldHalved,
  faSkull,
  faStar,
  faTree,
  type IconDefinition,
  faBoxArchive,
} from '@fortawesome/free-solid-svg-icons';

/** Everything the game may draw an icon for. */
export type IconName =
  /** The GRIND portal — the way out of town. */
  | 'portal'
  /** A gate through a rampart. */
  | 'gate'
  /** Where the player arrives. */
  | 'entry'
  /** The way onward, at the far end of a zone. */
  | 'exit'
  /** A rampart or wall segment, on a map or legend. */
  | 'wall'
  /** A dropped item. */
  | 'loot'
  /** An elite monster. */
  | 'elite'
  /** A dead thing. */
  | 'death'
  /** The Forge — heavy weapons and plate. */
  | 'forge'
  /** The Tannery — bows, daggers and leather. */
  | 'tannery'
  /** The Atelier — staves, wands and cloth. */
  | 'atelier'
  /** The Woodworks — shields, bows and staves. */
  | 'woodworks'
  /** The Arcane Vault — jewellery and infusions. */
  | 'vault'
  /** The Stash — where everything you are not carrying lives. */
  | 'stash';

const ICONS: Record<IconName, IconDefinition> = {
  portal: faDungeon,
  gate: faArchway,
  entry: faLocationDot,
  exit: faDoorOpen,
  wall: faShieldHalved,
  loot: faGem,
  elite: faStar,
  death: faSkull,
  forge: faHammer,
  tannery: faFeather,
  atelier: faBottleDroplet,
  woodworks: faTree,
  vault: faGem,
  stash: faBoxArchive,
};

/** One icon's geometry: a path in its own coordinate box. */
export interface IconPath {
  /** The `d` attribute of a single `<path>`. */
  d: string;
  /** The icon's own coordinate system, for a `viewBox`. */
  width: number;
  height: number;
}

/**
 * The path data for an icon.
 *
 * FontAwesome stores it as `[width, height, ligatures, unicode, path]`, and the
 * path may be an array when an icon has more than one shape. Joining is correct
 * here: every icon in the set above is single-path, and a joined pair still draws
 * — it would only lose per-shape fill rules, which none of these have.
 */
export function iconPath(name: IconName): IconPath {
  const def = ICONS[name];
  const [width, height, , , path] = def.icon;
  return {
    d: Array.isArray(path) ? path.join(' ') : path,
    width,
    height,
  };
}

/** Every icon name, for tests and legends. */
export const ICON_NAMES = Object.keys(ICONS) as IconName[];
